import { useState, useEffect } from 'react';
import { useAuth } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, RefreshCw, MoreVertical,
  Building2, Link, GripVertical, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const defaultLogo = {
  id: '', name: '', logo_url: '', website: '', order: 0, is_active: true
};

export const ClientLogosManager = () => {
  const { token } = useAuth();
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchLogos(); }, []);

  const fetchLogos = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/client-logos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogos(res.data);
    } catch (error) {
      toast.error('Failed to load client logos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.name || !item.logo_url) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (item.isNew) {
        await axios.post(`${API}/api/admin/client-logos`, item, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client logo added!');
      } else {
        await axios.put(`${API}/api/admin/client-logos/${item.id}`, item, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client logo updated!');
      }
      setEditModal({ open: false, item: null });
      fetchLogos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/admin/client-logos/${deleteDialog.item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client logo deleted');
      setDeleteDialog({ open: false, item: null });
      fetchLogos();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, isNew: false } : { ...defaultLogo, id: `logo-${Date.now()}`, order: logos.length + 1, isNew: true }
    });
  };

  const toggleActive = async (logo) => {
    try {
      await axios.put(`${API}/api/admin/client-logos/${logo.id}`, { ...logo, is_active: !logo.is_active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLogos();
      toast.success(logo.is_active ? 'Logo hidden' : 'Logo activated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6" data-testid="client-logos-manager">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-500" />
                Client Logos
              </CardTitle>
              <CardDescription>
                Manage logos displayed in the "Why Clients Trust Us" section
              </CardDescription>
            </div>
            <Button onClick={() => openEditModal()} className="bg-sky-500 hover:bg-sky-600" data-testid="add-logo-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Logo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">{logos.length} client logos</p>
            <Button variant="outline" onClick={fetchLogos} size="sm">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logos Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      ) : logos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No client logos yet</p>
            <Button onClick={() => openEditModal()} variant="outline">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Logo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {logos.map((logo) => (
            <Card 
              key={logo.id} 
              className={`overflow-hidden ${!logo.is_active ? 'opacity-50 border-dashed' : ''}`}
              data-testid={`logo-card-${logo.id}`}
            >
              <div className="aspect-square relative bg-slate-50 p-4 flex items-center justify-center">
                {logo.logo_url ? (
                  <img 
                    src={logo.logo_url} 
                    alt={logo.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-slate-300" />
                )}
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="secondary" className="w-7 h-7">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModal(logo)}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleActive(logo)}>
                        {logo.is_active ? 'Hide' : 'Show'}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeleteDialog({ open: true, item: logo })} 
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <span className="absolute bottom-2 left-2 text-xs text-slate-400">#{logo.order}</span>
              </div>
              <CardContent className="p-3">
                <p className="font-medium text-sm text-slate-900 truncate">{logo.name}</p>
                {logo.website && (
                  <a 
                    href={logo.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-sky-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    <Link className="w-3 h-3" /> Website
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add Client Logo' : 'Edit Client Logo'}</DialogTitle>
            <DialogDescription>Add logos of your trusted clients and partners</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input 
                  value={editModal.item.name} 
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, name: e.target.value } })} 
                  placeholder="Company Name"
                  data-testid="logo-name-input"
                />
              </div>

              <ImageUploader
                label="Logo Image *"
                value={editModal.item.logo_url}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, logo_url: url } })}
                allowFreeCrop={true}
                token={token}
              />

              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Link className="w-4 h-4" /> Website URL (Optional)</Label>
                <Input 
                  value={editModal.item.website || ''} 
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, website: e.target.value } })} 
                  placeholder="https://company.com"
                  data-testid="logo-website-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input 
                    type="number" 
                    value={editModal.item.order} 
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })} 
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch 
                    checked={editModal.item.is_active} 
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })} 
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600" data-testid="save-logo-btn">
              <Save className="w-4 h-4 mr-2" /> Save Logo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Logo?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the logo for "{deleteDialog.item?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientLogosManager;
